/**
 * Network / delivery prefs — layered transport controls (NET-REL-003/006).
 * Stored locally only; never uploaded (SPEC §19 privacy by default).
 */

import {
  composeEffectiveRelays,
  resolveCommunityRelays,
  setActiveRelayOverrides,
  setPublicMeshEnabled,
} from "@veto/transport";
import type { Contact } from "@veto/domain";
import type { BootstrapRelay } from "@veto/protocol";

export interface NetPrefs {
  /**
   * Community relays (NET-REL-006) — part of the default pool (sealed envelopes
   * reveal no sender/conversation); turning this off leaves only operated relays.
   */
  publicMesh: boolean;
  /**
   * Direct WebRTC channels (SPEC §11): opened automatically only with contacts
   * whose safety number you verified — a direct link reveals network addresses.
   */
  peerMesh: boolean;
  /** User-added relays (Settings → Network). */
  customRelays: string[];
  /** Relays auto-adopted by relay auto-recovery when everything was down. */
  autoRelays: string[];
  /** Bootstrap relays the user disabled. */
  disabledRelays: string[];
}

const KEY = "veto.net.prefs";

export const DEFAULT_NET_PREFS: NetPrefs = {
  publicMesh: true,
  peerMesh: true,
  customRelays: [],
  autoRelays: [],
  disabledRelays: [],
};

export function loadNetPrefs(): NetPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_NET_PREFS };
    const parsed = JSON.parse(raw) as Partial<NetPrefs>;
    const isRelayUrl = (u: string) => /^(wss?|https?):\/\//i.test(u);
    return {
      publicMesh: parsed.publicMesh !== false,
      peerMesh: parsed.peerMesh !== false,
      customRelays: Array.isArray(parsed.customRelays)
        ? parsed.customRelays.filter(isRelayUrl).slice(0, 16)
        : [],
      autoRelays: Array.isArray(parsed.autoRelays)
        ? parsed.autoRelays.filter(isRelayUrl).slice(0, 12)
        : [],
      disabledRelays: Array.isArray(parsed.disabledRelays) ? parsed.disabledRelays.slice(0, 32) : [],
    };
  } catch {
    return { ...DEFAULT_NET_PREFS };
  }
}

/** Fired after network preferences change so the relay pool re-plans. */
export const NET_PREFS_EVENT = "veto:netprefs";

export function saveNetPrefs(prefs: NetPrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* private mode — keep in-memory behaviour for this session */
  }
  try {
    window.dispatchEvent(new Event(NET_PREFS_EVENT));
  } catch {
    /* non-browser test env */
  }
}

export interface ResolvedRelayPlan {
  private: BootstrapRelay[];
  community: BootstrapRelay[];
  publicMesh: boolean;
  peerMesh: boolean;
}

/**
 * Apply prefs to the transport layer (overrides + public-mesh gate) and return
 * the effective plan used by publish + subscribe paths.
 */
export function applyNetPrefs(prefs: NetPrefs = loadNetPrefs()): ResolvedRelayPlan {
  setPublicMeshEnabled(prefs.publicMesh);
  // Only USER relays join the private tier. Auto-adopted community relays stay
  // in the inbox/subscribe pool (useRelayInbox merges autoRelays) — treating
  // them as primary would mislabel community sends as green "Relay" instead
  // of the honest amber "Public" (NET-REL-006 visual boundary).
  setActiveRelayOverrides({
    customUrls: prefs.customRelays,
    disabledUrls: prefs.disabledRelays,
  });
  return {
    private: composeEffectiveRelays(),
    community: prefs.publicMesh ? resolveCommunityRelays() : [],
    publicMesh: prefs.publicMesh,
    peerMesh: prefs.peerMesh,
  };
}

/** SPEC §11: a direct link reveals network addresses to the contact — only with an accepted,
 *  verified, unblocked contact, and only while "Direct connections" is on. Both sides check. */
export function directLinkAllowed(
  contact: Pick<Contact, "requestState" | "blockedAt" | "verificationState">,
  peerMeshOn = loadNetPrefs().peerMesh,
): boolean {
  return (
    peerMeshOn && contact.requestState !== "pending" && !contact.blockedAt && contact.verificationState === "verified"
  );
}
